import axios from "axios";
import type { TicketStatusId, TicketResponsibilityRoleId } from "../tickets";
import { RouteMode, RouteStrategy } from "../generated/enums";
import { makeConfigApi } from "./makeConfigApi";

export { RouteMode, RouteStrategy };

export type RoleConfigurationId = string & { __brand: "RoleConfigurationId" };
export type ProjectRoleId = string & { __brand: "ProjectRoleId" };

export type ProjectRole = {
    id: ProjectRoleId;
    name: string;
    order: number;
    /** Routing rules that send tickets to this role, as target or fallback. */
    usedBy: number;
};

export type RoleConfiguration = {
    id: RoleConfigurationId;
    name: string;
    roles: ProjectRole[];
};

/** One per status on a project configuration. A credited target ignores mode and roleId. */
export type TicketStatusRoute = {
    statusId: TicketStatusId;
    mode: RouteMode;
    strategy: RouteStrategy;
    roleId: ProjectRoleId | null;
    creditedRoleId: TicketResponsibilityRoleId | null;
    fallbackRoleId: ProjectRoleId | null;
};

export type RoleApi = {
    getRoleConfigurations(): Promise<RoleConfiguration[]>;
    createRoleConfiguration(name: string): Promise<RoleConfiguration>;
    editRoleConfiguration(config: RoleConfigurationId, name: string): Promise<void>;
    deleteRoleConfiguration(config: RoleConfigurationId): Promise<void>;

    createRole(config: RoleConfigurationId, name: string): Promise<ProjectRole>;
    editRole(config: RoleConfigurationId, role: ProjectRoleId, name: string): Promise<void>;
    reorderRoles(config: RoleConfigurationId, roleIds: ProjectRoleId[]): Promise<void>;
    deleteRole(config: RoleConfigurationId, role: ProjectRoleId): Promise<void>;
};

const configApi = makeConfigApi<RoleConfiguration>("roles");

export const roleApi: RoleApi = {
    getRoleConfigurations: configApi.list,
    createRoleConfiguration: configApi.create,
    editRoleConfiguration: configApi.edit,
    deleteRoleConfiguration: configApi.remove,

    async createRole(config, name) {
        const { data: role } = await axios.post<ProjectRole>(
            `/api/v0/project-configurations/roles/${config}/roles`,
            { name }
        );
        return role;
    },
    async editRole(config, role, name) {
        await axios.put(`/api/v0/project-configurations/roles/${config}/roles/${role}`, { name });
    },
    async reorderRoles(config, roleIds) {
        await axios.put(`/api/v0/project-configurations/roles/${config}/roles/order`, roleIds);
    },
    async deleteRole(config, role) {
        await axios.delete(`/api/v0/project-configurations/roles/${config}/roles/${role}`);
    },
};
